import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { VagaListagem } from "@/types/vaga";

export const VAGA_COLUMN_PAGE_SIZE = 30;

interface VagaColumnPage {
  rows: VagaListagem[];
}

/**
 * Carrega UMA coluna do kanban de /vagas de forma paginada (scroll infinito),
 * em vez de trazer todas as ~527 vagas de uma vez. Cada status é uma query
 * independente: a coluna pinta a 1ª página (~30 cards) na hora e carrega mais
 * ao rolar.
 *
 * Usa o RPC get_vagas_kanban_page (M131), que pagina por (status, created_at,
 * id) via índice parcial e agrega posições/negociações SÓ das ~30 vagas da
 * página. Antes a coluna paginava direto sobre a view v_vagas_listagem, cujo
 * LEFT JOIN v_vaga_card_metricas recomputava a agregação de TODAS as vagas a
 * cada página/coluna (seq scan de ~4,4k posições). O RPC devolve exatamente as
 * mesmas colunas da view (RETURNS SETOF v_vagas_listagem).
 *
 * O TOTAL da coluna NÃO vem daqui (não usamos count=exact: recomputaria a
 * agregação inteira por coluna). O total vem do useVagasCounts (RPC barato
 * GROUP BY status sobre vagas_base).
 *
 * Paginação por offset com o índice (status, created_at, id). Trocar por keyset
 * é uma mudança localizada aqui (e no RPC) se uma coluna passar de dezenas de
 * milhares.
 */
export function useVagaColumn(status: string, agenciaId: string) {
  return useInfiniteQuery({
    queryKey: ["vagas-column", status, agenciaId],
    initialPageParam: 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async ({ pageParam }): Promise<VagaColumnPage> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("get_vagas_kanban_page", {
        p_status: status,
        p_agencia_id: agenciaId || null,
        p_limit: VAGA_COLUMN_PAGE_SIZE,
        p_offset: pageParam,
      });
      if (error) throw error;
      return { rows: (data ?? []) as unknown as VagaListagem[] };
    },
    getNextPageParam: (lastPage, allPages) => {
      // Página cheia => provavelmente há mais; página parcial => acabou.
      const loaded = allPages.reduce((n, p) => n + p.rows.length, 0);
      return lastPage.rows.length === VAGA_COLUMN_PAGE_SIZE ? loaded : undefined;
    },
  });
}
